import type { ArtifactKind } from './utils/artifactKinds'

// Re-exported from the artifact registry so the union can never drift from
// the kinds the client actually knows how to transform and render.
export type { ArtifactKind }

export type TextBlock = { type: 'text'; text: string }
export type ToolUseBlock = {
  type: 'tool_use'
  id: string
  name: string
  input: unknown
}
export type ToolResultBlock = {
  type: 'tool_result'
  tool_use_id: string
  content: string
  is_error?: boolean
}

export type ContentBlock = TextBlock | ToolUseBlock | ToolResultBlock

export type ChatRole = 'user' | 'assistant'

export interface ChatMessage {
  id: number
  role: ChatRole
  position: number
  contentText: string | null
  contentJson: ContentBlock[]
  model?: string | null
  usage?: Record<string, unknown> | null
  // USD micros (1 USD = 1_000_000 micros) — computed server-side by
  // lukita's ChatMessageSerializer so the client doesn't need a local
  // pricing table. Nil on synthetic messages with no real model call.
  costMicros?: number | null
  createdAt?: string
  // Who sent this message, so a shared thread shows who said what. Email is
  // the label (users have no name yet); null on assistant messages. `mine` is
  // true when the current viewer sent it, so the UI can show "You".
  senderEmail?: string | null
  mine?: boolean
}

// Mirror of lukita's ChatSession::AGENTS and daystrom's AGENTS dict.
// Adding an agent means adding it here, on lukita, and on daystrom.
export type AgentName = 'transactions'

export const DEFAULT_AGENT: AgentName = 'transactions'

// The user-facing accuracy toggle (Default vs High Accuracy) was removed:
// every chat now runs in High Accuracy mode. The choice is made server-side
// in daystrom (see FORCE_HIGH_ACCURACY in its `model_selection`), so the
// frontend no longer models the mode, persists it, or sends it over the
// wire. The two-model capability still lives on daystrom — re-enabling the
// toggle here means re-adding the control plus flipping that daystrom flag.

export interface ChatSession {
  id: string
  projectId: number
  title: string | null
  agent: AgentName
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  // Chats are shared across everyone with access to the project. These fields
  // let the sidebar attribute a thread to its owner, split Mine vs Team, and
  // surface case-level pins. `mine` is computed server-side against the viewer.
  createdByEmail: string | null
  mine: boolean
  pinned: boolean
  pinnedAt: string | null
  pinnedByEmail: string | null
}

export interface ChatSessionWithMessages extends ChatSession {
  messages: ChatMessage[]
  // Set when a turn is streaming server-side at load time (e.g. a second tab
  // opened mid-turn). Lets the client show the working state and reconcile to
  // the final answer instead of looking dead.
  activeTurnId: string | null
  // Report edit cards cancelled in this project that have not expired yet.
  cancelledReportEditTokens?: string[]
}

export interface ToolCallStatus {
  id: string
  name: string
  ok: boolean | null
  errorCode?: string | null
  startedAt?: number | null
  finishedAt?: number | null
  // Which model step this call belongs to (0-based). Used to break tool-groups
  // at step boundaries during streaming so the per-step usage chip lands under
  // the right cluster of calls. Only set on live drafts; absent on persisted
  // messages (which carry usage per-message, not per-step).
  stepIndex?: number
}

export interface StepUsage {
  step: number
  model?: string | null
  stopReason?: string | null
  inputTokens: number
  outputTokens: number
  cacheReadInputTokens?: number | null
  cacheCreationInputTokens?: number | null
  // USD micros for this step — computed by daystrom and forwarded via
  // the `step_usage` SSE event so the live draft cost chip is accurate
  // during streaming without a client-side pricing table.
  costMicros: number
}

export interface ApplyFiltersSpec {
  filters: Array<{
    id: string
    field: string
    operator: string
    value: unknown
    value2?: unknown
  }>
  clear: boolean
}

export interface ArtifactRecord {
  toolUseId: string
  artifactId: string
  kind: ArtifactKind
  spec: unknown
}

// Wire-protocol type contract for the chat SSE stream.
//
// `chat-protocol.ts` is autogenerated by daystrom's
// `bin/emit_sse_types.py` script and vendored here. Re-run with
// `--frontend-out` to refresh both copies in one shot. Don't edit the
// generated file by hand.
//
// `ChatStreamMessage` is a discriminated union — switching on `.name`
// narrows `.data` to the matching payload, and TS flags any new event
// the backend adds that we haven't handled yet.
export type {
  ChatStreamEventName,
  ChatStreamMessage as SseEvent,
} from './types/chat-protocol'
export * from './types/chat-protocol'
